import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { checkIsPlatformAdmin, isFieldStaffUser } from './authRoles';
import { ensureStaffRoleInJwt } from './authStaffRoleSync';
import { fetchPanelAccess, hasPanelAccess } from './panelAccess';
import {
  needsMfaChallenge,
  getPrimaryVerifiedTotpFactor,
  verifyTotpCode,
  mfaErrorMessage,
} from './mfa';
import './Login.css';
import logo from './assets/newlogo.png';
import dukkan from './assets/dukkan.jpg';
import dukkan2 from './assets/dukkan2.jpg';

const slideImages = [dukkan, dukkan2];

async function routeAfterAuth(navigate, sessionUser) {
  let user = sessionUser;
  user = await ensureStaffRoleInJwt(supabase, user);
  if (await checkIsPlatformAdmin(supabase, user)) {
    navigate('/dashboard', { replace: true });
    return;
  }
  const access = await fetchPanelAccess(supabase);
  if (hasPanelAccess(access)) {
    navigate('/dashboard', { replace: true });
    return;
  }
  if (isFieldStaffUser(user)) {
    navigate('/barista', { replace: true });
    return;
  }
  await supabase.auth.signOut();
  throw new Error(
    'Bu hesap isletme sahibi (admin), barista veya mudur olarak tanimlanmadi. Isletme sahibiniz ile iletisime gecin.',
  );
}

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [slideIndex, setSlideIndex] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaFactorId, setMfaFactorId] = useState(null);
  const [step, setStep] = useState('password'); // password | mfa
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.setAttribute('name', 'robots');
    meta.setAttribute('content', 'noindex, nofollow');
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  useEffect(() => {
    if (location.state?.adminOnly) {
      setError('Yonetim paneline sadece isletme sahibi (admin) erisebilir; saha personeli barista/mudur girisi kullanir.');
    } else if (location.state?.mfaRequired) {
      setError('İki adımlı doğrulama gerekli. Kodunuzu girin veya tekrar giriş yapın.');
    }
  }, [location.state]);

  useEffect(() => {
    let cancelled = false;

    const routeFromSession = async (session) => {
      if (!session) return;
      try {
        if (await needsMfaChallenge()) {
          if (cancelled) return;
          const factor = await getPrimaryVerifiedTotpFactor();
          if (cancelled) return;
          if (factor?.id) {
            setMfaFactorId(factor.id);
            setStep('mfa');
            setError('');
          }
          return;
        }
        await routeAfterAuth(navigate, session.user);
      } catch (err) {
        if (!cancelled) setError(err.message || String(err));
      }
    };

    supabase.auth.getSession().then(({ data }) => routeFromSession(data.session));

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (_event === 'SIGNED_OUT') {
        setStep('password');
        setMfaFactorId(null);
        setMfaCode('');
        return;
      }
      routeFromSession(session);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [navigate]);

  useEffect(() => {
    const id = window.setInterval(() => {
      setSlideIndex((i) => (i + 1) % slideImages.length);
    }, 6500);
    return () => window.clearInterval(id);
  }, []);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !password) return;

    setLoading(true);
    const { data, error: authError } = await supabase.auth.signInWithPassword({
      email: trimmed,
      password,
    });

    if (authError) {
      setLoading(false);
      setError(authError.message);
      return;
    }

    try {
      if (await needsMfaChallenge()) {
        const factor = await getPrimaryVerifiedTotpFactor();
        if (!factor?.id) {
          throw new Error('Hesapta 2FA tanımlı ama doğrulama faktörü okunamadı.');
        }
        setMfaFactorId(factor.id);
        setStep('mfa');
        setMfaCode('');
        setLoading(false);
        return;
      }
      await routeAfterAuth(navigate, data?.user);
    } catch (err) {
      setError(mfaErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!mfaFactorId) {
      setError('Doğrulama faktörü bulunamadı. Tekrar giriş yapın.');
      return;
    }

    setLoading(true);
    try {
      await verifyTotpCode(mfaFactorId, mfaCode);
      const { data: { user } } = await supabase.auth.getUser();
      await routeAfterAuth(navigate, user);
    } catch (err) {
      setError(mfaErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleMfaBack = async () => {
    setLoading(true);
    setError('');
    await supabase.auth.signOut();
    setStep('password');
    setMfaFactorId(null);
    setMfaCode('');
    setPassword('');
    setLoading(false);
  };

  return (
    <div className="login-page">
      <div className="login-content">
        <div className="login-left">
          <div className="login-header">
            <img src={logo} alt="" className="login-logo" />
            <h1>{step === 'mfa' ? 'Doğrulama kodu' : 'Sign In'}</h1>
            <p>
              {step === 'mfa'
                ? 'Authenticator uygulamanızdaki 6 haneli kodu girin.'
                : 'Isletme sahibi (admin) veya saha personeli (barista / mudur) hesabi ile giris.'}
            </p>
          </div>

          {step === 'password' ? (
            <form className="login-form" onSubmit={handlePasswordSubmit} noValidate>
              <div className="login-form-group" style={{ '--index': 1 }}>
                <label htmlFor="email">Email address</label>
                <div className="login-input-with-icon">
                  <svg
                    className="login-input-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden
                  >
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="login-form-group" style={{ '--index': 2 }}>
                <label htmlFor="password">Password</label>
                <div className="login-input-with-icon">
                  <svg
                    className="login-input-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden
                  >
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0110 0v4" />
                  </svg>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    autoComplete="current-password"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              {error && (
                <div className="login-error" role="alert">
                  {error}
                </div>
              )}

              <button type="submit" className="login-btn" disabled={loading}>
                {loading ? 'Signing in…' : 'Continue'}
              </button>
              <p style={{ marginTop: 12, fontSize: 14, color: '#64748b' }}>
                Personel kaydi icin <Link to="/personnel-signup">Sign up</Link>
              </p>
            </form>
          ) : (
            <form className="login-form" onSubmit={handleMfaSubmit} noValidate>
              <div className="login-form-group" style={{ '--index': 1 }}>
                <label htmlFor="mfa-code">Authenticator kodu</label>
                <div className="login-input-with-icon">
                  <svg
                    className="login-input-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden
                  >
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0110 0v4" />
                  </svg>
                  <input
                    type="text"
                    id="mfa-code"
                    name="mfa-code"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="login-mfa-code"
                    required
                    disabled={loading}
                    autoFocus
                  />
                </div>
              </div>

              {error && (
                <div className="login-error" role="alert">
                  {error}
                </div>
              )}

              <button type="submit" className="login-btn" disabled={loading || mfaCode.length !== 6}>
                {loading ? 'Doğrulanıyor…' : 'Doğrula'}
              </button>
              <button
                type="button"
                className="login-btn login-btn--ghost"
                onClick={handleMfaBack}
                disabled={loading}
              >
                Geri
              </button>
            </form>
          )}
        </div>

        <div className="login-right">
          <div className="login-right-visual" aria-hidden>
            {slideImages.map((src, i) => (
              <img
                key={src}
                src={src}
                alt=""
                className={`login-right-slide-img ${i === slideIndex ? 'active' : ''}`}
              />
            ))}
          </div>
          <div className="login-right-scrim" aria-hidden />
          <div className="login-right-content" />
        </div>
      </div>
    </div>
  );
}

export default Login;
